/*
 * collection-order.js — global in stock first ordering for collection grids.
 *
 * THE PROBLEM
 * -----------
 * Sold out products must sort after in stock ones across the WHOLE collection. Liquid cannot do
 * this. `{% paginate %}` slices the collection before the template runs, so a page only holds its
 * own slice and can only order within it: the customer meets in stock, then sold out, then in
 * stock again on the next page, once per page. With the men's collection 54% out of stock that is
 * twelve alternations.
 *
 * Ordering globally means knowing every product's availability at once, and `paginate` will not
 * return more than 250 in one pass — asking for 400 silently yields 250, measured against this
 * store. The collection is 350. So the whole catalogue cannot reach a single Liquid render, and no
 * amount of template code changes that.
 *
 * THE APPROACH
 * ------------
 * Fetch the catalogue in 250 product pages through `templates/collection.bulk.liquid`, which is a
 * bare `{% layout none %}` list of the SAME `product-grid-item` cards the server would have
 * rendered. Two requests cover 350 (measured 2.1MB raw, 77KB over gzip, which every host applies).
 * Order the lot, then paginate it here with no page reloads.
 *
 * Cards come back as rendered HTML rather than from `/collections/<h>/products.json` on purpose:
 * the JSON would mean rebuilding the card in JavaScript (badges, compare-at pricing, money
 * formatting, swatches, responsive images) and keeping that copy in step with the Liquid one
 * forever. There is one card implementation, and it stays that way.
 *
 * PROGRESSIVE ENHANCEMENT — this never leaves the page worse than it found it:
 *   - the server already rendered a correct, complete, indexable page before this runs
 *   - pagination links keep their real hrefs, so crawlers and no-JS users walk the pages normally
 *   - every failure path returns silently and leaves the server's markup untouched
 *
 * The ordering is presentational only. It does not hide products, change what is in the
 * collection, or touch anything server side.
 */
(function () {
  'use strict';

  var GRID = '#product-grid';
  var CONTAINER = '#ProductGridContainer';
  var PAGER = '.paginatoin-area'; // theme's spelling, do not correct it
  var MAX_BULK_PAGES = 8; // 2000 products. A backstop against a paginate loop, never reached here.

  // Cards for the current collection+sort+filter state. In memory, not sessionStorage: paging is
  // client side so nothing reloads, and 350 cards of HTML would be a poor use of a 5MB quota.
  var cache = null; // { key: string, items: HTMLLIElement[] }
  var inflight = null; // { key: string, promise: Promise } — dedupes concurrent loads

  /*
   * The observer is disconnected around our own writes rather than gated by a boolean.
   *
   * A flag CANNOT work here, and the failed attempt is worth recording. MutationObserver delivers
   * its callback as a microtask, so this:
   *
   *     applying = true; grid.replaceChildren(...); applying = false;
   *
   * sets the flag back down synchronously, long before the callback runs. The callback therefore
   * always observed `false`, treated our own write as the theme's, and re-applied — which wrote to
   * the grid again, which queued another record. Measured at ~496 grid replacements per second,
   * forever. It never showed up as network traffic because the fetch cache hit every time, and the
   * page looked fine in a screenshot; what it broke was anything that depends on a card surviving
   * longer than 2ms. Hover never registered (the node under the cursor was replaced before :hover
   * could resolve, so `.single-product-wrap:hover .product-action` never matched) and the Snapmint
   * EMI line was destroyed on every pass right after it rendered.
   *
   * `disconnect()` empties the pending record queue as part of its contract, so reconnecting after
   * the write discards everything we caused and nothing else.
   */
  var observer = null;
  var watchHost = null;
  var debounce = null;

  function pauseObserver() {
    if (observer) observer.disconnect();
    clearTimeout(debounce);
  }

  function resumeObserver() {
    if (observer && watchHost) observer.observe(watchHost, { childList: true, subtree: true });
  }

  function grid() { return document.querySelector(GRID); }

  /*
   * Identity of the current product set. Sort order and filters change WHICH products come back
   * and in what order, so a change to either has to invalidate the cache and refetch. `page` is
   * excluded because paging is exactly what we are handling in memory.
   */
  function stateKey() {
    var p = new URLSearchParams(location.search);
    p.delete('page');
    p.delete('view');
    p.sort();
    return location.pathname + '?' + p.toString();
  }

  function currentPage() {
    var n = parseInt(new URLSearchParams(location.search).get('page'), 10);
    return n > 0 ? n : 1;
  }

  function pageSize(g) {
    var n = parseInt(g.getAttribute('data-page-size'), 10);
    return n > 0 ? n : 30;
  }

  /* Fetch one 250 product slice and return its <li> elements. */
  function fetchBulkPage(page) {
    var p = new URLSearchParams(location.search);
    p.delete('page');
    p.set('view', 'bulk');
    p.set('page', String(page));

    // No custom headers here on purpose. The section emits a matching
    // `<link rel="preload" as="fetch" crossorigin>` for these exact URLs so the download starts
    // during HTML parse instead of after the deferred script runs; adding a header risks the
    // browser treating this as a different request and fetching the same bytes twice.
    return fetch(location.pathname + '?' + p.toString(), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('bulk ' + page + ' -> ' + r.status);
        return r.text();
      })
      .then(function (html) {
        // The fragment is bare <li> elements. Parsing it through a <ul> keeps them intact;
        // DOMParser on a full document would hoist them out of table/list context.
        var ul = document.createElement('ul');
        ul.innerHTML = html;
        var marker = /<!--acbulk:(\d+):(\d+)-->/.exec(html);
        return {
          items: Array.prototype.slice.call(ul.querySelectorAll('li.st-col-item')),
          pages: marker ? parseInt(marker[2], 10) : 1
        };
      });
  }

  /* Walk every 250 product slice, then order: in stock first, each group keeping collection order. */
  function loadAll() {
    var key = stateKey();
    if (cache && cache.key === key) return Promise.resolve(cache.items);

    // Share one load between concurrent callers. Without this, a second apply() arriving before
    // the first finished started its own set of fetches: measured 4 requests for a 2 slice
    // collection, since neither run had populated the cache yet.
    if (inflight && inflight.key === key) return inflight.promise;

    // Page 1 reports the page count, so the rest go out together rather than in a chain. Matters
    // on /collections/all, which is four slices: four round trips in sequence became one.
    var run = fetchBulkPage(1).then(function (first) {
      var total = Math.min(first.pages, MAX_BULK_PAGES);
      var rest = [];
      for (var p = 2; p <= total; p++) rest.push(fetchBulkPage(p));

      return Promise.all(rest).then(function (pages) {
        // Concat in page order, never in completion order, or the collection's own sort is lost.
        var all = first.items;
        for (var i = 0; i < pages.length; i++) all = all.concat(pages[i].items);

        // Partition rather than sort: two passes are stable by construction, so products keep the
        // collection's own order (price descending here) inside each group.
        var live = [];
        var gone = [];
        for (var j = 0; j < all.length; j++) {
          (all[j].getAttribute('data-a') === '1' ? live : gone).push(all[j]);
        }
        cache = { key: key, items: live.concat(gone) };
        return cache.items;
      });
    });

    inflight = { key: key, promise: run };
    // Clear on settle either way, so a failed load is retried rather than cached as pending.
    run.catch(function () {}).then(function () {
      if (inflight && inflight.promise === run) inflight = null;
    });
    return run;
  }

  /*
   * Re-apply the per-card state the theme only sets once, at DOMContentLoaded, over whatever cards
   * existed then. Clicks are delegated on document so they keep working on injected cards, but
   * this initial pass is not, and without it a wishlisted product would show an empty heart.
   * Mirrors theme.wishlist.js.
   */
  function restoreWishlistState(scope) {
    var saved;
    try {
      saved = JSON.parse(localStorage.getItem('wishlist') || '[]');
    } catch (e) {
      return;
    }
    if (!saved || !saved.length) return;
    scope.querySelectorAll('.wishlist-custom-action[data-handle]').forEach(function (el) {
      if (saved.indexOf(el.getAttribute('data-handle')) >= 0) {
        el.classList.add('active-wishlist');
        el.classList.remove('wishlist-custom-action');
      }
    });
  }

  /*
   * Re-render the Snapmint "or ₹5434/Month — Buy on EMI" line on the cards we just injected.
   *
   * Snapmint (loaded from layout/theme.liquid when `settings.snapmint_enable` is on) writes that
   * line into each card at runtime; it is NOT in the Liquid, so it is absent from anything we
   * fetch. Its bundle scans ONCE on load, with a few bounded retry timers and no MutationObserver,
   * so replacing the grid leaves every card without an EMI line permanently.
   *
   * `collectionList(cardSelector)` is its own rescan entry point, and the bundle is a classic
   * script with no IIFE wrapper, so that top-level declaration is a real global. It already
   * guards each card with `if (!k.querySelector('.snap_dp_list'))`, so calling it again is
   * idempotent and will not double-inject.
   *
   * The selector is `snapOptions.categoryWidgetInfo` from the merchant bundle. It cannot be read
   * back at runtime: `snapOptions` is declared with `const` so it never lands on `window`, and the
   * code blanks `categoryWidgetInfo` immediately after its own first call. Hence the literal. It
   * is the theme's own card class, so it is stable from our side; if Snapmint ever reconfigures to
   * a different root, the EMI line goes missing again and this is the line to update.
   *
   * Snapmint also binds its own rescan to a pagination click, but that binding is on the pager
   * node we replace, so it cannot be relied on. Calling this on every render covers it.
   *
   * Guarded and wrapped: absent on local dev (the script is domain gated) and when the setting is
   * off, and a throw inside a third-party bundle must not take the grid down with it.
   */
  var SNAP_CARD_SELECTOR = '.single-product-wrap';

  function refreshEmiWidget() {
    if (typeof window.collectionList !== 'function') return;
    try {
      window.collectionList(SNAP_CARD_SELECTOR);
    } catch (e) {
      if (window.console) console.warn('[collection-order] EMI rescan failed:', e);
    }
  }

  /* Rebuild the pager to match snippets/pagination.liquid, with real hrefs. */
  function renderPager(page, pages) {
    var area = document.querySelector(PAGER);
    if (!area) return;
    if (pages < 2) { area.innerHTML = ''; return; }

    function href(n) {
      var p = new URLSearchParams(location.search);
      if (n === 1) p.delete('page');
      else p.set('page', String(n));
      var q = p.toString();
      return location.pathname + (q ? '?' + q : '');
    }

    // Same window Shopify uses: first, last, and the current page with a neighbour either side.
    var want = {};
    want[1] = want[pages] = 1;
    for (var d = -1; d <= 1; d++) if (page + d >= 1 && page + d <= pages) want[page + d] = 1;
    var nums = Object.keys(want).map(Number).sort(function (a, b) { return a - b; });

    var html = '';
    if (page > 1) {
      html += '<li class="prev np-icon"><a href="' + href(page - 1) + '" class="previous" aria-label="Previous"><i class="feather-arrow-left"></i></a></li>';
    }
    var last = 0;
    nums.forEach(function (n) {
      if (last && n > last + 1) html += '<li class="number"><a>&hellip;</a></li>';
      html += n === page
        ? '<li class="number active"><a>' + n + '</a></li>'
        : '<li class="number"><a href="' + href(n) + '">' + n + '</a></li>';
      last = n;
    });
    if (page < pages) {
      html += '<li class="next np-icon"><a href="' + href(page + 1) + '" class="next" aria-label="Next"><i class="feather-arrow-right"></i></a></li>';
    }
    area.innerHTML = '<ul class="list-unstyled pagination-box">' + html + '</ul>';
  }

  function show(items, page) {
    var g = grid();
    if (!g) return;
    var size = pageSize(g);
    var pages = Math.max(1, Math.ceil(items.length / size));
    if (page > pages) page = pages;

    pauseObserver();
    g.replaceChildren.apply(g, items.slice((page - 1) * size, page * size));
    restoreWishlistState(g);
    renderPager(page, pages);
    refreshEmiWidget();
    resumeObserver();

    // Lets any theme or app script re-init against the new cards.
    document.dispatchEvent(new CustomEvent('ac:grid-updated', { detail: { page: page, pages: pages } }));
  }

  function apply() {
    var g = grid();
    if (!g || g.getAttribute('data-global-order') !== 'true') return;
    loadAll()
      .then(function (items) { show(items, currentPage()); })
      .catch(function (e) {
        // Server-rendered grid stays exactly as it was.
        if (window.console) console.warn('[collection-order] left server order in place:', e.message);
      });
  }

  /* Page through without reloading; the hrefs stay real so this is enhancement, not replacement. */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest(PAGER + ' a[href]');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    var g = grid();
    if (!g || g.getAttribute('data-global-order') !== 'true' || !cache) return;

    e.preventDefault();
    history.pushState({}, '', a.getAttribute('href'));
    show(cache.items, currentPage());
    var top = document.querySelector(CONTAINER);
    if (top) window.scrollTo({ top: top.getBoundingClientRect().top + window.scrollY - 120, behavior: 'smooth' });
  });

  window.addEventListener('popstate', function () {
    if (cache && cache.key === stateKey()) show(cache.items, currentPage());
    else apply();
  });

  /*
   * The theme replaces #ProductGridContainer wholesale when filters or sort change. That hands us
   * a fresh server-ordered grid, so re-apply. `applying` keeps our own writes from retriggering.
   */
  function watch() {
    watchHost = document.querySelector(CONTAINER);
    if (!watchHost) return;
    observer = new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(apply, 60);
    });
    resumeObserver();
  }

  function start() { apply(); watch(); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
